import React from 'react'
import { displayNamesOfEventTypes, GENDER_LIST_ONBOARDING, STATUS_SESSION_CANCELLED, STATUS_SESSION_BOOKED, BOOKING_STATUSES } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { OFFLINE_PAYMENT_METHODS_LIST } from 'src/consumer/constants'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)
import { Button, Box, Typography, Divider, Chip, useTheme, useMediaQuery } from '@mui/material'
import Person from '@mui/icons-material/Person'
import CalendarToday from '@mui/icons-material/CalendarToday'
import AccessTime from '@mui/icons-material/AccessTime'
import VideoCall from '@mui/icons-material/VideoCall'
import LocationOn from '@mui/icons-material/LocationOn'
import Payment from '@mui/icons-material/Payment'
import OpenInNew from '@mui/icons-material/OpenInNew'
import PlayArrow from '@mui/icons-material/PlayArrow'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import { getOrdinalSuffixForSuperScript } from 'src/consumer/helpers/utilFunctions'
import { styled } from '@mui/material/styles'
import { SESSION_PLATFORM_ONLINE, SESSION_PLATFORM_OFFLINE } from 'src/consumer/constants'

// Styled Components
const PopoverContainer = styled(Box)`
  width: 320px;
  max-width: 90vw;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 255, 0.98) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(124, 58, 237, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: calc(100vw - 32px);
    max-width: calc(100vw - 32px);
  }
`

const HeaderSection = styled(Box)`
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%);
  padding: 12px 16px;
  border-bottom: none;
  
  @media (max-width: 768px) {
    padding: 10px 14px;
  }
`

const PatientName = styled(Typography)`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 6px;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`

const PatientInfo = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`

const InfoChip = styled(Chip)`
  height: 26px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.3);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
`

const ContentSection = styled(Box)`
  padding: 0;
  display: flex;
  flex-direction: column;
`

const CompactInfoGroup = styled(Box)`
  padding: 10px 16px;
  background: rgba(250, 245, 255, 0.6);
  border-bottom: 1px solid rgba(124, 58, 237, 0.15);
  
  @media (max-width: 768px) {
    padding: 8px 14px;
  }
  
  &:last-of-type {
    border-bottom: none;
  }
  
  &:nth-of-type(even) {
    background: rgba(245, 243, 255, 0.5);
  }
`

const CompactInfoRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  flex-wrap: wrap;
  
  &:not(:last-child) {
    margin-bottom: 4px;
  }
`

const CompactValue = styled(Box)`
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
`

const ActionSection = styled(Box)`
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(250, 245, 255, 0.8) 0%, rgba(245, 243, 255, 0.8) 100%);
  border-top: 2px solid rgba(124, 58, 237, 0.2);
  display: flex;
  flex-direction: column;
  gap: 10px;
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    gap: 8px;
  }
`

const PrimaryActions = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SecondaryActions = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`

const HeroButton = styled(Button)`
  padding: 12px 20px;
  border-radius: 10px;
  font-weight: 700;
  text-transform: none;
  font-size: 14px;
  box-shadow: 0 4px 16px rgba(124, 58, 237, 0.35);
  transition: all 0.2s ease;
  min-height: 44px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(124, 58, 237, 0.45);
  }
  
  @media (max-width: 768px) {
    padding: 12px 18px;
    font-size: 13px;
    min-height: 48px;
  }
`

const SecondaryButton = styled(Button)`
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
  font-size: 13px;
  color: rgba(124, 58, 237, 0.95);
  border-color: rgba(124, 58, 237, 0.4);
  min-height: 40px;
  
  &:hover {
    background: rgba(124, 58, 237, 0.1);
    border-color: rgba(124, 58, 237, 0.6);
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 12px;
    min-height: 44px;
  }
`

const TertiaryButton = styled(Button)`
  padding: 8px 14px;
  border-radius: 6px;
  font-weight: 500;
  text-transform: none;
  font-size: 12px;
  min-height: 36px;
  justify-content: flex-start;
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 11px;
    min-height: 40px;
  }
`

export default function SlotPopupSummary({
  clickedBookedSlotData,
  setClickedBookedSlotAnchor,
  getAndSetBookedSlotsOfSelectedDate,
  markBookedSessionAsCompletedFunction,
  setCancelDialogData,
  setMarkCompletedDialogData, // New prop for mark completed dialog
  platformOptions,
  bookedSlotActionText,
  pendingActionsOfPerson,
  forMentor,
  hideStartButton,
  hideMarkedCompletedButton,
  goToEvent,
  onOpenMainDrawer, // New prop to open main drawer
}) {
  const patientNameRaw = clickedBookedSlotData?.userName || clickedBookedSlotData?.patientInfo?.name || 'Unknown Patient'
  // Capitalize patient name - capitalize first letter of each word, handle edge cases
  const patientName = patientNameRaw
    ? patientNameRaw
        .trim()
        .split(/\s+/)
        .map(word => {
          if (!word) return word
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        })
        .join(' ')
    : 'Unknown Patient'
  const patientInfo = clickedBookedSlotData?.patientInfo
  const genderLabel = patientInfo?.gender 
    ? GENDER_LIST_ONBOARDING?.find((item) => item.value === patientInfo.gender)?.label 
    : null
  const age = patientInfo?.dateOfBirth ? getAgeFromDobWithTime(patientInfo.dateOfBirth) : null
  const patientNumber = patientInfo?.patientNumber
  const visitNumber = clickedBookedSlotData?.patientMeetingSeqNumber
  const visitSuffix = visitNumber ? getOrdinalSuffixForSuperScript(visitNumber) : ''
  
  const eventTypeName = displayNamesOfEventTypes?.[clickedBookedSlotData?.eventType] || 'Session'
  const platformName = platformOptions?.find(
    (platformItem) => platformItem?.id === clickedBookedSlotData?.meetingSrc,
  )?.name || (clickedBookedSlotData?.meetingSrc === SESSION_PLATFORM_ONLINE ? 'Online' : 'Offline')
  
  const startTime = clickedBookedSlotData?.startTime 
    ? dayjs.utc(clickedBookedSlotData.startTime).local()
    : null
  const endTime = clickedBookedSlotData?.endTime 
    ? dayjs.utc(clickedBookedSlotData.endTime).local()
    : null
  
  const isOnline = clickedBookedSlotData?.meetingSrc === SESSION_PLATFORM_ONLINE
  const isOffline = clickedBookedSlotData?.meetingSrc === SESSION_PLATFORM_OFFLINE
  
  const feePaid = clickedBookedSlotData?.feePaid
  const currency = clickedBookedSlotData?.currency
  const paymentMode = clickedBookedSlotData?.feePaymentMode
    ? OFFLINE_PAYMENT_METHODS_LIST.find((item) => item.id === clickedBookedSlotData.feePaymentMode)?.title
    : null
  
  const appointmentNumber = clickedBookedSlotData?.appointmentNumber
  const chamberRoomName = clickedBookedSlotData?.chamberRoomName
  
  const isCompleted = clickedBookedSlotData?.markedCompleted
  const isCancelled = clickedBookedSlotData?.status === STATUS_SESSION_CANCELLED
  // CRITICAL: Only allow session opening for booked status (status=booked)
  // Events/Bookings which do not have status=booked cannot be opened (session page cannot be opened)
  // Only mentor portal has the session opening functionality
  const isBooked = clickedBookedSlotData?.status === STATUS_SESSION_BOOKED || clickedBookedSlotData?.status === BOOKING_STATUSES.BOOKED
  const isSupportPortal = typeof window !== 'undefined' && window.location?.pathname?.includes('/support')
  
  const screeningInfo = (clickedBookedSlotData?.previousSteps || [])?.find(
    (item) => item?.refType === 'screening' && item?.status === 'completed',
  )

  // Calculate duration
  const durationMinutes = startTime && endTime 
    ? dayjs.duration(endTime.diff(startTime)).asMinutes()
    : null
  
  // Format duration for display (hours and minutes)
  const formatDuration = (minutes) => {
    if (!minutes || minutes < 1) return '0 mins'
    const hours = Math.floor(minutes / 60)
    const mins = Math.floor(minutes % 60)
    if (hours > 0 && mins > 0) {
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${mins} ${mins === 1 ? 'min' : 'mins'}`
    } else if (hours > 0) {
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`
    } else {
      return `${mins} ${mins === 1 ? 'min' : 'mins'}`
    }
  }
  
  // Calculate if running late (current time is past scheduled start time)
  const now = dayjs()
  const isRunningLate = startTime && now.isAfter(startTime) && !isCompleted
  const minutesLate = isRunningLate 
    ? Math.floor(dayjs.duration(now.diff(startTime)).asMinutes())
    : null
  const lateDurationFormatted = minutesLate !== null ? formatDuration(minutesLate) : null
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <PopoverContainer>
      {/* Header Section */}
      <HeaderSection>
        <PatientName>{patientName}</PatientName>
        <PatientInfo>
          {genderLabel && age && (
            <InfoChip 
              label={`${genderLabel}/${age}Y`} 
              size="small"
              icon={<Person sx={{ fontSize: 12, color: '#ffffff' }} />}
            />
          )}
          {patientNumber && (
            <InfoChip 
              label={`#${patientNumber}`} 
              size="small"
            />
          )}
          {visitNumber && (
            <InfoChip 
              label={`${visitNumber}${visitSuffix} Visit`} 
              size="small"
            />
          )}
        </PatientInfo>
      </HeaderSection>

      {/* Content Section - Compact Combined Information */}
      <ContentSection>
        {/* Combined Date & Time Row */}
        {startTime && (
          <CompactInfoGroup>
            <CompactInfoRow>
              <CalendarToday sx={{ fontSize: 14, color: 'rgba(124, 58, 237, 0.8)', flexShrink: 0 }} />
              <CompactValue>
                <strong>{startTime.format('MMM DD, YYYY')}</strong>
                <span style={{ color: 'rgba(124, 58, 237, 0.5)', margin: '0 4px' }}>•</span>
                <AccessTime sx={{ fontSize: 13, color: 'rgba(124, 58, 237, 0.7)', flexShrink: 0 }} />
                <strong>{startTime.format('h:mm A')}</strong>
                {durationMinutes && (
                  <span style={{ color: 'rgba(124, 58, 237, 0.6)', fontSize: '11px', marginLeft: '4px' }}>
                    ({durationMinutes}m)
                  </span>
                )}
                {isRunningLate && lateDurationFormatted && (
                  <Chip 
                    label={`${lateDurationFormatted} late`} 
                    size="small" 
                    sx={{ 
                      height: 18, 
                      fontSize: '9px',
                      backgroundColor: 'rgba(239, 68, 68, 0.15)',
                      color: 'rgba(220, 38, 38, 1)',
                      fontWeight: 700,
                      ml: 0.5,
                    }} 
                  />
                )}
                {appointmentNumber && (
                  <>
                    <span style={{ color: 'rgba(124, 58, 237, 0.5)', margin: '0 4px' }}>•</span>
                    <span style={{ color: 'rgba(124, 58, 237, 0.7)', fontSize: '11px', fontWeight: 600 }}>#{appointmentNumber}</span>
                  </>
                )}
              </CompactValue>
            </CompactInfoRow>
          </CompactInfoGroup>
        )}

        {/* Combined Session Type & Platform Row */}
        <CompactInfoGroup>
            <CompactInfoRow>
              <Chip
                label={eventTypeName}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '10px',
                  fontWeight: 700,
                  backgroundColor: 'rgba(124, 58, 237, 0.15)',
                  color: 'rgba(124, 58, 237, 1)',
                  border: '1px solid rgba(124, 58, 237, 0.3)',
                  flexShrink: 0,
                }}
              />
              {isOnline ? (
                <VideoCall sx={{ fontSize: 16, color: 'rgba(16, 185, 129, 0.9)', flexShrink: 0 }} />
              ) : (
                <LocationOn sx={{ fontSize: 16, color: 'rgba(245, 158, 11, 0.9)', flexShrink: 0 }} />
              )}
              <CompactValue>
                <span style={{ fontWeight: 600 }}>{platformName}</span>
                {chamberRoomName && (
                  <>
                    <span style={{ color: 'rgba(124, 58, 237, 0.5)', margin: '0 4px' }}>•</span>
                    <span style={{ color: 'rgba(124, 58, 237, 0.8)', fontSize: '12px', fontWeight: 500 }}>{chamberRoomName}</span>
                  </>
                )}
              </CompactValue>
            </CompactInfoRow>
        </CompactInfoGroup>

        {/* Payment (if applicable) */}
        {isOffline && feePaid && (
          <CompactInfoGroup>
            <CompactInfoRow>
              <Payment sx={{ fontSize: 14, color: 'rgba(124, 58, 237, 0.8)', flexShrink: 0 }} />
              <CompactValue>
                <strong style={{ color: 'rgba(124, 58, 237, 1)' }}>{currency?.toUpperCase()} {feePaid}</strong>
                {paymentMode && (
                  <span style={{ color: 'rgba(124, 58, 237, 0.6)', fontSize: '11px', marginLeft: '6px', fontWeight: 500 }}>
                    ({paymentMode})
                  </span>
                )}
              </CompactValue>
            </CompactInfoRow>
          </CompactInfoGroup>
        )}

        {/* Additional Info (if any) */}
        {(screeningInfo || bookedSlotActionText) && (
          <CompactInfoGroup>
            {screeningInfo && (
              <CompactInfoRow>
                <CompactValue sx={{ fontSize: '11px', color: 'rgba(124, 58, 237, 0.7)', fontWeight: 500 }}>
                  Screened by {screeningInfo?.stepHandlerName}
                </CompactValue>
              </CompactInfoRow>
            )}
            {bookedSlotActionText && (
              <CompactInfoRow>
                <CompactValue sx={{ fontSize: '11px' }}>
                  <span style={{ textDecoration: 'underline', color: 'rgba(124, 58, 237, 0.8)', fontWeight: 600 }}>{bookedSlotActionText}</span>
                  {pendingActionsOfPerson?.find(
                    (item) => item?.type === 'refund' && item?.bookedEventId === clickedBookedSlotData?.id,
                  ) ? (
                    <Chip label="Pending" size="small" color="warning" sx={{ height: 18, fontSize: '9px', ml: 0.5 }} />
                  ) : (
                    <Chip label="Done" size="small" color="success" sx={{ height: 18, fontSize: '9px', ml: 0.5 }} />
                  )}
                </CompactValue>
              </CompactInfoRow>
            )}
          </CompactInfoGroup>
        )}
      </ContentSection>

      {/* Action Section */}
      {forMentor && !isSupportPortal && (
        <ActionSection>
          <PrimaryActions>
            {/* Hero Action: Start/View Session - Only show for booked status */}
            {!hideStartButton && isBooked && (
              <HeroButton
                variant="contained"
                color="primary"
                startIcon={<PlayArrow />}
                onClick={() => {
                  goToEvent(clickedBookedSlotData)
                  setClickedBookedSlotAnchor(null)
                }}
                sx={{
                  background: 'linear-gradient(135deg, rgba(124, 58, 237, 1) 0%, rgba(139, 92, 246, 1) 100%)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%)',
                  },
                }}
                fullWidth={isMobile}
              >
                {isCompleted ? 'View/Edit' : 'Start'} {eventTypeName}
              </HeroButton>
            )}

            {/* Open Main Drawer Button */}
            {onOpenMainDrawer && (
              <SecondaryButton
                variant="outlined"
                startIcon={<OpenInNew />}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (onOpenMainDrawer && clickedBookedSlotData) {
                    onOpenMainDrawer(clickedBookedSlotData)
                  }
                  setTimeout(() => {
                    if (setClickedBookedSlotAnchor) {
                      setClickedBookedSlotAnchor(null)
                    }
                  }, 200)
                }}
                fullWidth={isMobile}
              >
                Other Actions
              </SecondaryButton>
            )}
          </PrimaryActions>

          {/* Secondary Actions */}
          <SecondaryActions>
            {/* Mark as Completed - Only show for booked status */}
            {!hideMarkedCompletedButton && !isCompleted && isBooked && (
              <TertiaryButton
                variant="text"
                startIcon={<CheckCircle sx={{ fontSize: 18, color: 'rgba(16, 185, 129, 0.9)' }} />}
                onClick={() => {
                  if (setMarkCompletedDialogData) {
                    setMarkCompletedDialogData(clickedBookedSlotData)
                  }
                  setClickedBookedSlotAnchor(null)
                }}
                sx={{
                  color: 'rgba(16, 185, 129, 0.9)',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'rgba(16, 185, 129, 0.08)',
                  },
                }}
                fullWidth={isMobile}
              >
                Mark as Completed
              </TertiaryButton>
            )}

            {/* Cancel Appointment */}
            {!isCancelled && (
              <TertiaryButton
                variant="text"
                startIcon={<Cancel sx={{ fontSize: 18, color: 'rgba(239, 68, 68, 0.9)' }} />}
                onClick={() => {
                  setCancelDialogData(clickedBookedSlotData)
                  setClickedBookedSlotAnchor(null)
                }}
                sx={{
                  color: 'rgba(239, 68, 68, 0.9)',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'rgba(239, 68, 68, 0.08)',
                  },
                }}
                fullWidth={isMobile}
              >
                Cancel Appointment
              </TertiaryButton>
            )}
          </SecondaryActions>
        </ActionSection>
      )}
    </PopoverContainer>
  )
}
